// Finds the document in the photo with OpenCV.js (free, loaded at runtime
// from opencv.org so it is not in the app bundle) and checks it is not cut off.

const OPENCV_URL = "https://docs.opencv.org/4.9.0/opencv.js";
const OPENCV_TIMEOUT_MS = 20000;

let cvPromise = null;

// Starts the ~10 MB download once. Resolves to null if it fails or is slow,
// so the caller can fall back to the pixel checks only.
export const loadOpenCv = () => {
  if (cvPromise) return cvPromise;

  cvPromise = new Promise((resolve) => {
    let timer;
    const done = (cv) => {
      clearTimeout(timer);
      // OpenCV's module object has its own `then`. Resolving a Promise with
      // it makes the Promise call it forever and freezes the page.
      if (cv && !(cv instanceof Promise)) delete cv.then;
      resolve(cv);
    };

    if (window.cv && window.cv.Mat) {
      done(window.cv);
      return;
    }

    timer = setTimeout(() => resolve(null), OPENCV_TIMEOUT_MS);

    const script = document.createElement("script");
    script.src = OPENCV_URL;
    script.async = true;
    script.onload = () => {
      const cv = window.cv;
      if (!cv) {
        done(null);
      } else if (cv.Mat) {
        done(cv);
      } else if (typeof cv.then === "function") {
        cv.then((ready) => {
          window.cv = ready;
          done(ready);
        });
      } else {
        cv.onRuntimeInitialized = () => done(cv);
      }
    };
    script.onerror = () => {
      cvPromise = null;
      done(null);
    };
    document.body.appendChild(script);
  });

  return cvPromise;
};

// Looks for a document-shaped outline: 4 corners, sensible proportions.
// Returns:
// { found, corners, box, areaRatio, tightCrop } for a whole document,
// { found: false, cutOff: true, areaRatio } for one running off the photo,
// { found: false, cutOff: false, anyShape } when no document was found.
// Corners and box are in the coordinates of `canvas`.
export const detectCard = (cv, canvas, t) => {
  const mats = [];
  const track = (m) => {
    mats.push(m);
    return m;
  };

  try {
    const src = track(cv.imread(canvas));
    const grey = track(new cv.Mat());
    cv.cvtColor(src, grey, cv.COLOR_RGBA2GRAY);
    cv.GaussianBlur(grey, grey, new cv.Size(5, 5), 0);
    const edges = track(new cv.Mat());
    cv.Canny(grey, edges, 50, 150);
    const kernel = track(cv.Mat.ones(3, 3, cv.CV_8U));
    cv.dilate(edges, edges, kernel);

    // RETR_LIST also returns inner outlines, so a card whose edge touches
    // nearby objects (keyboard keys, a table edge) is still found on its own.
    const contours = track(new cv.MatVector());
    const hierarchy = track(new cv.Mat());
    cv.findContours(
      edges,
      contours,
      hierarchy,
      cv.RETR_LIST,
      cv.CHAIN_APPROX_SIMPLE
    );

    const W = canvas.width;
    const H = canvas.height;
    const imageArea = W * H;
    const margin = Math.max(W, H) * t.edgeMarginRatio + 1;
    const onEdge = (p) =>
      p.x <= margin || p.y <= margin || p.x >= W - margin || p.y >= H - margin;

    let card = null;
    let cut = null;
    let anyShape = false;

    for (let i = 0; i < contours.size(); i++) {
      const c = contours.get(i);
      // Convex hull: a card running off the photo has an open outline whose
      // own area is near zero.
      const hull = new cv.Mat();
      cv.convexHull(c, hull);
      c.delete();
      const areaRatio = cv.contourArea(hull) / imageArea;

      if (areaRatio >= t.minCardAreaRatio) {
        anyShape = true;
        const approx = new cv.Mat();
        cv.approxPolyDP(hull, approx, 0.02 * cv.arcLength(hull, true), true);
        const points = [];
        for (let k = 0; k < approx.rows; k++) {
          points.push({
            x: approx.data32S[k * 2],
            y: approx.data32S[k * 2 + 1],
          });
        }

        if (points.length === 4 && isDocumentShape(points, W, H, t)) {
          const corners = orderCorners(points);
          if (!corners.some(onEdge)) {
            if (!card || areaRatio > card.areaRatio) {
              const r = cv.boundingRect(hull);
              card = {
                corners,
                areaRatio,
                box: { x: r.x, y: r.y, w: r.width, h: r.height },
              };
            }
          } else if (!cut || areaRatio > cut.areaRatio) {
            cut = { areaRatio };
          }
        }
        approx.delete();
      }
      hull.delete();
    }

    if (card) {
      return {
        found: true,
        ...card,
        tightCrop: card.areaRatio >= t.tightCropAreaRatio,
      };
    }
    // A document-shaped outline that only exists cut by the photo edge.
    // A document filling the whole photo (tight crop) is not cut off.
    if (cut && cut.areaRatio < t.tightCropAreaRatio) {
      return { found: false, cutOff: true, areaRatio: cut.areaRatio };
    }
    return { found: false, cutOff: false, anyShape };
  } finally {
    mats.forEach((m) => m.delete());
  }
};

// Rejects shapes that are not a document: long thin strips (door frames,
// curtains), very uneven sides, or a few points bunched together.
const isDocumentShape = (points, W, H, t) => {
  const [tl, tr, br, bl] = orderCorners(points);
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const top = dist(tl, tr);
  const bottom = dist(bl, br);
  const left = dist(tl, bl);
  const right = dist(tr, br);
  const minSide = Math.min(W, H) * t.minCardSideRatio;

  if (Math.min(top, bottom, left, right) < minSide) return false;
  if (Math.min(top, bottom) / Math.max(top, bottom) < t.minOppositeSideRatio)
    return false;
  if (Math.min(left, right) / Math.max(left, right) < t.minOppositeSideRatio)
    return false;

  const width = (top + bottom) / 2;
  const height = (left + right) / 2;
  const aspect = Math.max(width, height) / Math.min(width, height);
  return aspect <= t.maxCardAspect;
};

// Top-left, top-right, bottom-right, bottom-left.
const orderCorners = (pts) => {
  const bySum = [...pts].sort((a, b) => a.x + a.y - (b.x + b.y));
  const byDiff = [...pts].sort((a, b) => a.y - a.x - (b.y - b.x));
  return [bySum[0], byDiff[0], bySum[3], byDiff[3]];
};
